// LP用のエントリーポイント
import '@/styles/application.css'

// ========================================
// Scroll Animation (Intersection Observer)
// ========================================
function initScrollAnimations(): void {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in')
        }
      })
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    }
  )

  document.querySelectorAll('.scroll-animate').forEach((el) => {
    observer.observe(el)
  })
}

// ========================================
// Terminal Typing Animation
// ========================================
interface TerminalCommand {
  text: string
  isComment?: boolean
  output?: string
  outputClass?: string
}

const terminalCommands: TerminalCommand[] = [
  { text: '# 1. ログイン', isComment: true },
  {
    text: 'docker login registry.konreji.com',
    output: 'Login Succeeded',
    outputClass: 'terminal-success',
  },
  { text: '', isComment: true },
  { text: '# 2. タグ付け', isComment: true },
  {
    text: 'docker tag myapp:latest registry.konreji.com/username/myapp:latest',
  },
  { text: '', isComment: true },
  { text: '# 3. プッシュ', isComment: true },
  {
    text: 'docker push registry.konreji.com/username/myapp:latest',
    output:
      'The push refers to repository [registry.konreji.com/username/myapp]\nlatest: digest: sha256:abc123... size: 2048',
    outputClass: 'terminal-output',
  },
]

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function typeText(
  element: HTMLElement,
  text: string,
  speed = 30
): Promise<void> {
  for (const char of text) {
    element.textContent += char
    await delay(speed + Math.random() * 20)
  }
}

async function runTerminalAnimation(container: HTMLElement): Promise<void> {
  container.innerHTML = '' // Clear any existing content

  for (const cmd of terminalCommands) {
    const line = document.createElement('div')
    line.className = 'terminal-line mb-1'

    if (cmd.isComment) {
      line.className += ' terminal-comment'
      if (cmd.text) {
        await typeText(line, cmd.text, 20)
      }
      container.appendChild(line)
      await delay(200)
      continue
    }

    // Add prompt
    const prompt = document.createElement('span')
    prompt.className = 'terminal-prompt'
    prompt.textContent = '$ '
    line.appendChild(prompt)

    // Add command text with typing effect
    const cmdSpan = document.createElement('span')
    cmdSpan.className = 'terminal-command'
    line.appendChild(cmdSpan)

    // Add cursor
    const cursor = document.createElement('span')
    cursor.className = 'typing-cursor'
    line.appendChild(cursor)

    container.appendChild(line)

    // Type the command with syntax highlighting
    await typeCommand(cmdSpan, cmd.text)

    // Remove cursor after typing
    cursor.remove()

    // Show output if any
    if (cmd.output) {
      await delay(300)
      const outputLine = document.createElement('div')
      outputLine.className = cmd.outputClass || 'terminal-output'
      outputLine.innerHTML = cmd.output.replace(/\n/g, '<br>')
      container.appendChild(outputLine)
    }

    await delay(500)
  }

  // Add final blinking cursor
  const finalCursor = document.createElement('div')
  finalCursor.className = 'terminal-line mt-2'
  finalCursor.innerHTML = '<span class="terminal-prompt">$ </span><span class="typing-cursor"></span>'
  container.appendChild(finalCursor)
}

async function typeCommand(element: HTMLElement, command: string): Promise<void> {
  // Parse and colorize the command
  const parts = parseCommand(command)

  for (const part of parts) {
    const span = document.createElement('span')
    span.className = part.class
    element.appendChild(span)
    await typeText(span, part.text, 25)
  }
}

interface CommandPart {
  text: string
  class: string
}

function parseCommand(command: string): CommandPart[] {
  const parts: CommandPart[] = []

  // Simple syntax highlighting rules
  const words = command.split(' ')

  for (let i = 0; i < words.length; i++) {
    const word = words[i]

    if (i > 0) {
      parts.push({ text: ' ', class: '' })
    }

    // Highlight registry URL
    if (word.includes('registry.konreji.com')) {
      const segments = word.split('/')
      parts.push({ text: segments[0], class: 'terminal-highlight' })
      for (let j = 1; j < segments.length; j++) {
        parts.push({ text: '/', class: '' })
        if (segments[j].includes(':')) {
          const [name, tag] = segments[j].split(':')
          parts.push({ text: name, class: 'terminal-orange' })
          parts.push({ text: ':', class: '' })
          parts.push({ text: tag, class: 'terminal-purple' })
        } else {
          parts.push({ text: segments[j], class: 'terminal-orange' })
        }
      }
    }
    // Highlight image:tag format
    else if (word.includes(':') && !word.startsWith('-')) {
      const [name, tag] = word.split(':')
      parts.push({ text: name, class: 'terminal-orange' })
      parts.push({ text: ':', class: '' })
      parts.push({ text: tag, class: 'terminal-purple' })
    }
    // Docker commands
    else if (['docker', 'login', 'push', 'pull', 'tag'].includes(word)) {
      parts.push({ text: word, class: 'terminal-command' })
    }
    // Default
    else {
      parts.push({ text: word, class: '' })
    }
  }

  return parts
}

// ========================================
// Initialize on DOM Ready
// ========================================
document.addEventListener('DOMContentLoaded', () => {
  // Initialize scroll animations
  initScrollAnimations()

  // Initialize terminal typing animation when it comes into view
  const terminalDemo = document.getElementById('terminal-demo')
  if (terminalDemo) {
    let hasAnimated = false

    const terminalObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          hasAnimated = true
          terminalObserver.disconnect()
          runTerminalAnimation(terminalDemo)
        }
      },
      { threshold: 0.3 }
    )

    terminalObserver.observe(terminalDemo)
  }

  // Smooth scroll for anchor links
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', (e) => {
      e.preventDefault()
      const target = document.querySelector(
        (anchor as HTMLAnchorElement).getAttribute('href') || ''
      )
      if (target) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
    })
  })
})
